import {
  useQuery,
  useMutation,
  useQueryClient,
  keepPreviousData,
  UseQueryResult,
} from '@tanstack/react-query';
import api from '../lib/api';
import { Pagination } from '../types/api';
import {
  Course,
  CourseDetail,
  CourseStatusCounts,
  CoursesListResponse,
  CourseResponse,
  CourseDetailResponse,
  CoursesQueryParams,
  CreateCourseData,
  UpdateCourseData,
  CourseSection,
  CourseSectionWithModules,
  SectionResponse,
  SectionCreateData,
  SectionUpdateData,
  CourseModule,
  ModuleResponse,
  ModuleCreateData,
  ModuleUpdateData,
  ReorderRequest,
} from '../types/course';
import {
  CreateAssignmentData,
  AssignmentResponse,
  Assignment,
  SendAssignmentsData,
  SendAssignmentsResult,
  UpdateAssignmentData,
} from '../types/assignment';
import { SuccessResponse } from '../types/api';
import { normalizePagination } from '../api/client';

// ─── Courses ────────────────────────────────────────────────────────────

/**
 * Fetch paginated list of courses
 * GET /api/v1/courses
 */
const EMPTY_STATUS_COUNTS: CourseStatusCounts = { draft: 0, published: 0, archived: 0 };

export const fetchCourses = async (
  params: CoursesQueryParams = {}
): Promise<{ courses: Course[]; pagination: Pagination; statusCounts: CourseStatusCounts }> => {
  const queryParams = new URLSearchParams();

  if (params.page) queryParams.append('page', params.page.toString());
  if (params.per_page) queryParams.append('per_page', params.per_page.toString());
  if (params.search) queryParams.append('search', params.search);
  if (params.status) {
    const statuses = Array.isArray(params.status) ? params.status : [params.status];
    statuses.forEach((s) => queryParams.append('status', s));
  }

  const response = await api.get<CoursesListResponse>(
    `/courses?${queryParams.toString()}`
  );
  return {
    courses: response.data.data.courses ?? [],
    pagination: normalizePagination(response.data.data.pagination),
    statusCounts: response.data.data.status_counts ?? EMPTY_STATUS_COUNTS,
  };
};

/**
 * Custom hook to fetch courses list with pagination and filters
 */
export const useCourses = (
  params: CoursesQueryParams = {}
): UseQueryResult<{ courses: Course[]; pagination: Pagination; statusCounts: CourseStatusCounts }, Error> => {
  return useQuery({
    queryKey: ['courses', params],
    queryFn: () => fetchCourses(params),
    // Always revalidate against the API on every filter/page switch instead of
    // serving a still-fresh cache entry...
    staleTime: 0,
    gcTime: 5 * 60 * 1000,
    refetchOnMount: 'always',
    // ...but keep the previous page's cards on screen during the refetch so the
    // grid cross-fades smoothly (isFetching drives the inline "Updating…" cue).
    placeholderData: keepPreviousData,
  });
};

/**
 * Fetch single course by ID with sections and modules
 * GET /api/v1/courses/:id
 */
export const fetchCourse = async (courseId: string): Promise<CourseDetail> => {
  const response = await api.get<CourseDetailResponse>(`/courses/${courseId}`);
  return response.data.data;
};

/**
 * Custom hook to fetch single course by ID
 */
export const useCourse = (
  courseId: string | undefined
): UseQueryResult<CourseDetail, Error> => {
  return useQuery({
    queryKey: ['courses', courseId],
    queryFn: () => fetchCourse(courseId!),
    enabled: courseId !== undefined,
    staleTime: 2 * 60 * 1000,
  });
};

/**
 * Create a new course
 * POST /api/v1/courses
 */
export const createCourse = async (data: CreateCourseData): Promise<Course> => {
  const response = await api.post<CourseResponse>('/courses', data);
  return response.data.data;
};

export const useCreateCourse = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: createCourse,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['courses'] });
    },
  });
};

/**
 * Update an existing course
 * PUT /api/v1/courses/:id
 */
export const updateCourse = async (
  courseId: string,
  data: UpdateCourseData
): Promise<Course> => {
  const response = await api.put<CourseResponse>(`/courses/${courseId}`, data);
  return response.data.data;
};

export const useUpdateCourse = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ courseId, data }: { courseId: string; data: UpdateCourseData }) =>
      updateCourse(courseId, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['courses', variables.courseId] });
      queryClient.invalidateQueries({ queryKey: ['courses'] });
    },
  });
};

/**
 * Render a sample personalized certificate for an uploaded design.
 * POST /api/v1/courses/:id/certificate-preview → PDF blob.
 */
export const fetchCertificatePreview = async (
  courseId: string,
  fileKey: string
): Promise<Blob> => {
  const response = await api.post<Blob>(
    `/courses/${courseId}/certificate-preview`,
    { file_key: fileKey },
    { responseType: 'blob' }
  );
  return response.data;
};

/**
 * Delete a course (soft delete if published with assignments)
 * DELETE /api/v1/courses/:id
 */
export const deleteCourse = async (courseId: string): Promise<SuccessResponse> => {
  const response = await api.delete<{ data: SuccessResponse }>(`/courses/${courseId}`);
  return response.data.data;
};

export const useDeleteCourse = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: deleteCourse,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['courses'] });
    },
  });
};

/**
 * Assign a course to learners/groups
 * POST /api/v1/courses/:id/assign
 */
export const assignCourse = async (
  courseId: string,
  data: CreateAssignmentData
): Promise<AssignmentResponse['data']> => {
  const response = await api.post<AssignmentResponse>(`/courses/${courseId}/assign`, data);
  return response.data.data;
};

export const useAssignCourse = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ courseId, data }: { courseId: string; data: CreateAssignmentData }) =>
      assignCourse(courseId, data),
    onSuccess: (_data, variables) => {
      // Course detail picks up auto-publish; assignments list refreshes for
      // the future drafts page.
      queryClient.invalidateQueries({ queryKey: ['courses'] });
      queryClient.invalidateQueries({ queryKey: ['courses', variables.courseId] });
      queryClient.invalidateQueries({ queryKey: ['courses', variables.courseId, 'assignments'] });
    },
  });
};

// ─── Assignments management ─────────────────────────────────────────────
//
// Service plumbing for GET / PUT / DELETE /courses/:id/assignments and
// POST /courses/:id/assignments/send. /configure does not consume these
// today — they're staged for the upcoming drafts-management page.

interface AssignmentsListResponse {
  status: 'success';
  data: Assignment[];
}

export const fetchAssignments = async (
  courseId: string,
  params: { notified?: boolean } = {}
): Promise<Assignment[]> => {
  const qs = new URLSearchParams();
  if (params.notified !== undefined) qs.append('notified', params.notified.toString());
  const url = qs.toString()
    ? `/courses/${courseId}/assignments?${qs.toString()}`
    : `/courses/${courseId}/assignments`;
  const response = await api.get<AssignmentsListResponse>(url);
  return response.data.data ?? [];
};

export const useAssignmentsList = (
  courseId: string | undefined,
  params: { notified?: boolean } = {}
) =>
  useQuery({
    queryKey: ['courses', courseId, 'assignments', params],
    queryFn: () => fetchAssignments(courseId!, params),
    enabled: !!courseId,
    staleTime: 30 * 1000,
  });

export const updateAssignment = async (
  courseId: string,
  assignmentId: string,
  data: UpdateAssignmentData
): Promise<Assignment> => {
  const response = await api.put<{ status: 'success'; data: Assignment }>(
    `/courses/${courseId}/assignments/${assignmentId}`,
    data
  );
  return response.data.data;
};

export const useUpdateAssignment = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      courseId,
      assignmentId,
      data,
    }: {
      courseId: string;
      assignmentId: string;
      data: UpdateAssignmentData;
    }) => updateAssignment(courseId, assignmentId, data),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: ['courses', variables.courseId, 'assignments'],
      });
    },
  });
};

export const deleteAssignment = async (
  courseId: string,
  assignmentId: string
): Promise<void> => {
  await api.delete(`/courses/${courseId}/assignments/${assignmentId}`);
};

export const revokeAssignment = async (
  courseId: string,
  assignmentId: string
): Promise<SuccessResponse> => {
  const response = await api.post<{ data: SuccessResponse }>(
    `/courses/${courseId}/assignments/${assignmentId}/revoke`
  );
  return response.data.data;
};

/** Revoke a SENT assignment — the learner loses access, history stays. */
export const useRevokeAssignment = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ courseId, assignmentId }: { courseId: string; assignmentId: string }) =>
      revokeAssignment(courseId, assignmentId),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: ['courses', variables.courseId, 'assignments'],
      });
    },
  });
};

export const useDeleteAssignment = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ courseId, assignmentId }: { courseId: string; assignmentId: string }) =>
      deleteAssignment(courseId, assignmentId),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: ['courses', variables.courseId, 'assignments'],
      });
    },
  });
};

export const sendAssignments = async (
  courseId: string,
  data: SendAssignmentsData = {}
): Promise<SendAssignmentsResult> => {
  const response = await api.post<{ status: 'success'; data: SendAssignmentsResult }>(
    `/courses/${courseId}/assignments/send`,
    data
  );
  return response.data.data;
};

export const useSendAssignments = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ courseId, data }: { courseId: string; data?: SendAssignmentsData }) =>
      sendAssignments(courseId, data ?? {}),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['courses', variables.courseId] });
      queryClient.invalidateQueries({
        queryKey: ['courses', variables.courseId, 'assignments'],
      });
    },
  });
};

// ─── Sections ───────────────────────────────────────────────────────────

/**
 * Fetch all sections for a course
 * GET /api/v1/courses/:courseId/sections
 */
export const fetchSections = async (
  courseId: string
): Promise<CourseSectionWithModules[]> => {
  const response = await api.get<{ status: string; data: CourseSectionWithModules[] }>(
    `/courses/${courseId}/sections`
  );
  return response.data.data;
};

export const useSections = (
  courseId: string | undefined
): UseQueryResult<CourseSectionWithModules[], Error> => {
  return useQuery({
    queryKey: ['courses', courseId, 'sections'],
    queryFn: () => fetchSections(courseId!),
    enabled: !!courseId,
    staleTime: 60 * 1000,
  });
};

/**
 * Fetch a single section
 * GET /api/v1/courses/:courseId/sections/:sectionId
 */
export const fetchSection = async (
  courseId: string,
  sectionId: string
): Promise<CourseSectionWithModules> => {
  const response = await api.get<{ status: string; data: CourseSectionWithModules }>(
    `/courses/${courseId}/sections/${sectionId}`
  );
  return response.data.data;
};

export const useSection = (
  courseId: string | undefined,
  sectionId: string | undefined
): UseQueryResult<CourseSectionWithModules, Error> => {
  return useQuery({
    queryKey: ['courses', courseId, 'sections', sectionId],
    queryFn: () => fetchSection(courseId!, sectionId!),
    enabled: !!courseId && !!sectionId,
    staleTime: 60 * 1000,
  });
};

/**
 * Add section to course
 * POST /api/v1/courses/:courseId/sections
 */
export const createSection = async (
  courseId: string,
  data: SectionCreateData
): Promise<CourseSection> => {
  const response = await api.post<SectionResponse>(`/courses/${courseId}/sections`, data);
  return response.data.data;
};

export const useCreateSection = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ courseId, data }: { courseId: string; data: SectionCreateData }) =>
      createSection(courseId, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['courses', variables.courseId] });
    },
  });
};

/**
 * Update section
 * PUT /api/v1/courses/:courseId/sections/:sectionId
 */
export const updateSection = async (
  courseId: string,
  sectionId: string,
  data: SectionUpdateData
): Promise<CourseSection> => {
  const response = await api.put<SectionResponse>(
    `/courses/${courseId}/sections/${sectionId}`,
    data
  );
  return response.data.data;
};

export const useUpdateSection = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      courseId,
      sectionId,
      data,
    }: {
      courseId: string;
      sectionId: string;
      data: SectionUpdateData;
    }) => updateSection(courseId, sectionId, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['courses', variables.courseId] });
    },
  });
};

/**
 * Delete section (soft delete)
 * DELETE /api/v1/courses/:courseId/sections/:sectionId
 */
export const deleteSection = async (
  courseId: string,
  sectionId: string
): Promise<SuccessResponse> => {
  const response = await api.delete<{ data: SuccessResponse }>(
    `/courses/${courseId}/sections/${sectionId}`
  );
  return response.data.data;
};

export const useDeleteSection = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ courseId, sectionId }: { courseId: string; sectionId: string }) =>
      deleteSection(courseId, sectionId),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['courses', variables.courseId] });
    },
  });
};

/**
 * Reorder sections
 * PUT /api/v1/courses/:courseId/sections/reorder
 */
export const reorderSections = async (
  courseId: string,
  data: ReorderRequest
): Promise<SuccessResponse> => {
  const response = await api.put<{ data: SuccessResponse }>(
    `/courses/${courseId}/sections/reorder`,
    data
  );
  return response.data.data;
};

export const useReorderSections = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ courseId, data }: { courseId: string; data: ReorderRequest }) =>
      reorderSections(courseId, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['courses', variables.courseId] });
    },
  });
};

// ─── Modules ────────────────────────────────────────────────────────────

/**
 * Fetch all modules for a section
 * GET /api/v1/courses/:courseId/sections/:sectionId/modules
 */
export const fetchModules = async (
  courseId: string,
  sectionId: string
): Promise<CourseModule[]> => {
  const response = await api.get<{ status: string; data: CourseModule[] }>(
    `/courses/${courseId}/sections/${sectionId}/modules`
  );
  return response.data.data;
};

export const useModules = (
  courseId: string | undefined,
  sectionId: string | undefined
): UseQueryResult<CourseModule[], Error> => {
  return useQuery({
    queryKey: ['courses', courseId, 'sections', sectionId, 'modules'],
    queryFn: () => fetchModules(courseId!, sectionId!),
    enabled: !!courseId && !!sectionId,
    staleTime: 60 * 1000,
  });
};

/**
 * Fetch a single module
 * GET /api/v1/courses/:courseId/sections/:sectionId/modules/:moduleId
 */
export const fetchModule = async (
  courseId: string,
  sectionId: string,
  moduleId: string
): Promise<CourseModule> => {
  const response = await api.get<ModuleResponse>(
    `/courses/${courseId}/sections/${sectionId}/modules/${moduleId}`
  );
  return response.data.data;
};

export const useModule = (
  courseId: string | undefined,
  sectionId: string | undefined,
  moduleId: string | undefined
): UseQueryResult<CourseModule, Error> => {
  return useQuery({
    queryKey: ['courses', courseId, 'sections', sectionId, 'modules', moduleId],
    queryFn: () => fetchModule(courseId!, sectionId!, moduleId!),
    enabled: !!courseId && !!sectionId && !!moduleId,
    staleTime: 60 * 1000,
  });
};

/**
 * Add module to section
 * POST /api/v1/courses/:courseId/sections/:sectionId/modules
 */
export const createModule = async (
  courseId: string,
  sectionId: string,
  data: ModuleCreateData
): Promise<CourseModule> => {
  const response = await api.post<ModuleResponse>(
    `/courses/${courseId}/sections/${sectionId}/modules`,
    data
  );
  return response.data.data;
};

export const useCreateModule = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      courseId,
      sectionId,
      data,
    }: {
      courseId: string;
      sectionId: string;
      data: ModuleCreateData;
    }) => createModule(courseId, sectionId, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['courses', variables.courseId] });
    },
  });
};

/**
 * Update module
 * PUT /api/v1/courses/:courseId/sections/:sectionId/modules/:moduleId
 */
export const updateModule = async (
  courseId: string,
  sectionId: string,
  moduleId: string,
  data: ModuleUpdateData
): Promise<CourseModule> => {
  const response = await api.put<ModuleResponse>(
    `/courses/${courseId}/sections/${sectionId}/modules/${moduleId}`,
    data
  );
  return response.data.data;
};

export const useUpdateModule = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      courseId,
      sectionId,
      moduleId,
      data,
    }: {
      courseId: string;
      sectionId: string;
      moduleId: string;
      data: ModuleUpdateData;
    }) => updateModule(courseId, sectionId, moduleId, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['courses', variables.courseId] });
    },
  });
};

/**
 * Delete module (soft delete)
 * DELETE /api/v1/courses/:courseId/sections/:sectionId/modules/:moduleId
 */
export const deleteModule = async (
  courseId: string,
  sectionId: string,
  moduleId: string
): Promise<SuccessResponse> => {
  const response = await api.delete<{ data: SuccessResponse }>(
    `/courses/${courseId}/sections/${sectionId}/modules/${moduleId}`
  );
  return response.data.data;
};

export const useDeleteModule = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      courseId,
      sectionId,
      moduleId,
    }: {
      courseId: string;
      sectionId: string;
      moduleId: string;
    }) => deleteModule(courseId, sectionId, moduleId),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['courses', variables.courseId] });
    },
  });
};

/**
 * Reorder modules within a section
 * PUT /api/v1/courses/:courseId/sections/:sectionId/modules/reorder
 */
export const reorderModules = async (
  courseId: string,
  sectionId: string,
  data: ReorderRequest
): Promise<SuccessResponse> => {
  const response = await api.put<{ data: SuccessResponse }>(
    `/courses/${courseId}/sections/${sectionId}/modules/reorder`,
    data
  );
  return response.data.data;
};

export const useReorderModules = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      courseId,
      sectionId,
      data,
    }: {
      courseId: string;
      sectionId: string;
      data: ReorderRequest;
    }) => reorderModules(courseId, sectionId, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['courses', variables.courseId] });
    },
  });
};
